import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { driver, Alignment, Config, DriveStep, Driver, PopoverDOM, Side } from "driver.js"
import "driver.js/dist/driver.css"
import "./Tour.module.scss"

type TourTarget = Element | string | (() => Element | null | undefined)
type TourPlacement = Side | `${Side}-${Alignment}`

export interface TourStep {
    target?: TourTarget
    title?: ReactNode
    description?: ReactNode
    placement?: TourPlacement
    nextButtonProps?: {
        children?: ReactNode
        disabled?: boolean
    }
    prevButtonProps?: {
        children?: ReactNode
        disabled?: boolean
    }
    onNext?: () => void
    onPrev?: () => void
    onClose?: () => void
}

export interface TourProps {
    open?: boolean
    defaultOpen?: boolean
    current?: number
    defaultCurrent?: number
    steps: TourStep[]
    onClose?: (current: number) => void
    onChange?: (current: number) => void
    disabledInteraction?: boolean
    mask?: boolean
    placement?: TourPlacement
    rootClassName?: string
    zIndex?: number
    nextButtonProps?: {
        children?: ReactNode
        disabled?: boolean
    }
    prevButtonProps?: {
        children?: ReactNode
        disabled?: boolean
    }
}

const DEFAULT_NEXT_TEXT = "Далее"
const DEFAULT_PREV_TEXT = "Назад"
const DEFAULT_DONE_TEXT = "Далее"

function isControlled<T>(value: T | undefined): value is T {
    return value !== undefined
}

function parsePlacement(placement?: TourPlacement): { side?: Side; align?: Alignment } {
    if (!placement) return {}

    const [side, align] = placement.split("-") as [Side, Alignment | undefined]

    return {
        side,
        align,
    }
}

function resolveTarget(target?: TourTarget): DriveStep["element"] | undefined {
    if (!target) return undefined

    if (typeof target === "function") {
        return () => target() as Element
    }

    return target
}

function getTargetElement(target?: TourTarget): Element | undefined {
    if (!target) return undefined

    if (typeof target === "string") {
        return document.querySelector(target) ?? undefined
    }

    if (typeof target === "function") {
        return target() ?? undefined
    }

    return target
}

function isElementVisible(element: Element): boolean {
    if (!element.isConnected || element.getClientRects().length === 0) return false

    const styles = window.getComputedStyle(element)

    return styles.display !== "none" && styles.visibility !== "hidden" && styles.opacity !== "0"
}

function isStepAvailable(step: TourStep): boolean {
    if (!step.target) return true

    const element = getTargetElement(step.target)

    return Boolean(element && isElementVisible(element))
}

function canRenderReactNode(node: ReactNode): boolean {
    return node !== undefined && node !== null && node !== false
}

const Tour = (props: TourProps) => {
    const {
        steps,
        disabledInteraction,
        mask = true,
        placement,
        rootClassName,
        zIndex,
    } = props

    const driverRef = useRef<Driver | null>(null)
    const silentDestroyRef = useRef(false)
    const resetProgressOnDestroyRef = useRef(false)
    const prepareInitialPopoverRef = useRef(false)
    const preparePopoverFrameRef = useRef<number>()
    const currentRef = useRef(props.defaultCurrent ?? 0)
    const renderedContainersRef = useRef<HTMLElement[]>([])
    const latestPropsRef = useRef(props)
    const [innerOpen, setInnerOpen] = useState(props.defaultOpen ?? false)
    const [innerCurrent, setInnerCurrent] = useState(props.defaultCurrent ?? 0)

    const mergedOpen = isControlled(props.open) ? props.open : innerOpen
    const mergedCurrent = isControlled(props.current) ? props.current : innerCurrent

    latestPropsRef.current = props
    currentRef.current = mergedCurrent
    const availableSteps = useMemo(() => steps.filter(isStepAvailable), [steps, mergedOpen])

    const unmountRenderedNodes = () => {
        renderedContainersRef.current.forEach((container) => {
            ReactDOM.unmountComponentAtNode(container)
        })
        renderedContainersRef.current = []
    }

    const setCurrent = (nextCurrent: number) => {
        if (!isControlled(latestPropsRef.current.current)) {
            setInnerCurrent(nextCurrent)
        }

        latestPropsRef.current.onChange?.(nextCurrent)
    }

    const closeTour = (currentIndex?: number) => {
        const activeIndex = currentIndex ?? driverRef.current?.getActiveIndex() ?? mergedCurrent

        if (!isControlled(latestPropsRef.current.open)) {
            setInnerOpen(false)
        }

        latestPropsRef.current.onClose?.(activeIndex)
    }

    const resetProgress = () => {
        currentRef.current = 0

        if (!isControlled(latestPropsRef.current.current)) {
            setInnerCurrent(0)
        }

        latestPropsRef.current.onChange?.(0)
    }

    const renderNode = (container: HTMLElement, node: ReactNode) => {
        if (!canRenderReactNode(node)) {
            container.innerHTML = ""
            return
        }

        ReactDOM.render(<>{node}</>, container)
        renderedContainersRef.current.push(container)
    }

    const renderPopoverContent = (popover: PopoverDOM, step: TourStep, currentIndex: number, totalSteps: number) => {
        unmountRenderedNodes()

        popover.title.innerHTML = ""
        popover.description.innerHTML = ""
        popover.progress.innerHTML = ""
        popover.wrapper.querySelector(".f3-tour-progress")?.remove()
        popover.footerButtons.querySelector(".f3-tour-finish-btn")?.remove()

        renderNode(popover.title, step.title)
        renderNode(popover.description, step.description)

        popover.wrapper.classList.add("f3-tour-popover")
        popover.wrapper.classList.toggle("f3-tour-popover--no-title", !canRenderReactNode(step.title))
        popover.progress.innerText = `${currentIndex + 1} из ${totalSteps}`

        const progressBlock = document.createElement("div")
        progressBlock.className = "f3-tour-progress"
        progressBlock.innerHTML = `
            <div class="f3-tour-progress__header">
                <span>Прогресс обучения</span>
                <span>${Math.round(((currentIndex + 1) / totalSteps) * 100)}%</span>
            </div>
            <div class="f3-tour-progress__track">
                <div class="f3-tour-progress__bar" style="width: ${((currentIndex + 1) / totalSteps) * 100}%"></div>
            </div>
        `

        popover.description.after(progressBlock)

        const finishButton = document.createElement("button")
        finishButton.type = "button"
        finishButton.className = "f3-tour-finish-btn"
        finishButton.innerText = "Завершить"
        finishButton.onclick = () => {
            resetProgressOnDestroyRef.current = true
            driverRef.current?.destroy()
        }

        popover.footerButtons.appendChild(finishButton)

        if (latestPropsRef.current.zIndex) {
            popover.wrapper.style.zIndex = String(latestPropsRef.current.zIndex + 1)
        }

        if (prepareInitialPopoverRef.current) {
            popover.wrapper.classList.add("f3-tour-popover--preparing")

            if (preparePopoverFrameRef.current) {
                window.cancelAnimationFrame(preparePopoverFrameRef.current)
            }

            preparePopoverFrameRef.current = window.requestAnimationFrame(() => {
                preparePopoverFrameRef.current = window.requestAnimationFrame(() => {
                    popover.wrapper.classList.remove("f3-tour-popover--preparing")
                    prepareInitialPopoverRef.current = false
                    preparePopoverFrameRef.current = undefined
                })
            })
        }
    }

    const driveSteps = useMemo<DriveStep[]>(() => {
        return availableSteps.map((step) => {
            const stepPlacement = parsePlacement(step.placement ?? placement)
            const nextButtonText = step.nextButtonProps?.children ?? props.nextButtonProps?.children
            const prevButtonText = step.prevButtonProps?.children ?? props.prevButtonProps?.children

            return {
                element: resolveTarget(step.target),
                disableActiveInteraction: disabledInteraction,
                popover: {
                    title: " ",
                    description: " ",
                    side: stepPlacement.side,
                    align: stepPlacement.align,
                    nextBtnText: typeof nextButtonText === "string" ? nextButtonText : DEFAULT_NEXT_TEXT,
                    prevBtnText: typeof prevButtonText === "string" ? prevButtonText : DEFAULT_PREV_TEXT,
                    doneBtnText: DEFAULT_DONE_TEXT,
                    onPopoverRender: (popover, opts) => {
                        const activeIndex = opts.state.activeIndex ?? 0
                        const isLastStep = activeIndex === availableSteps.length - 1

                        renderPopoverContent(popover, step, activeIndex, availableSteps.length)

                        const nextButtonDisabled = step.nextButtonProps?.disabled ?? props.nextButtonProps?.disabled
                        const prevButtonDisabled = step.prevButtonProps?.disabled ?? props.prevButtonProps?.disabled

                        popover.nextButton.disabled = Boolean(nextButtonDisabled)
                        popover.nextButton.style.display = isLastStep ? "none" : ""
                        popover.previousButton.disabled = Boolean(prevButtonDisabled)
                    },
                },
                onNextClick: () => {
                    step.onNext?.()

                    const driverInstance = driverRef.current
                    if (!driverInstance) return
                    if (driverInstance.isLastStep()) return

                    const nextIndex = (driverInstance.getActiveIndex() ?? 0) + 1
                    setCurrent(nextIndex)
                    driverInstance.moveNext()
                },
                onPrevClick: () => {
                    step.onPrev?.()

                    const driverInstance = driverRef.current
                    if (!driverInstance) return

                    const prevIndex = Math.max((driverInstance.getActiveIndex() ?? 0) - 1, 0)
                    setCurrent(prevIndex)
                    driverInstance.movePrevious()
                },
                onCloseClick: () => {
                    step.onClose?.()
                    driverRef.current?.destroy()
                },
            }
        })
    }, [availableSteps, placement, disabledInteraction, props.nextButtonProps, props.prevButtonProps])

    useEffect(() => {
        if (!mergedOpen || availableSteps.length === 0) {
            silentDestroyRef.current = true
            driverRef.current?.destroy()
            silentDestroyRef.current = false
            driverRef.current = null
            return
        }

        const driverConfig: Config = {
            steps: driveSteps,
            animate: true,
            smoothScroll: false,
            overlayColor: "#111827",
            allowClose: true,
            allowKeyboardControl: true,
            disableActiveInteraction: disabledInteraction,
            overlayOpacity: mask ? 0.7 : 0,
            stagePadding: 8,
            stageRadius: 4,
            showProgress: true,
            progressText: "{{current}} / {{total}}",
            popoverClass: rootClassName,
            onHighlighted: (_, __, opts) => {
                const activeIndex = opts.driver.getActiveIndex()

                if (activeIndex !== undefined && activeIndex !== currentRef.current) {
                    setCurrent(activeIndex)
                }
            },
            onDestroyed: () => {
                unmountRenderedNodes()

                if (resetProgressOnDestroyRef.current) {
                    resetProgress()
                    resetProgressOnDestroyRef.current = false
                }

                if (!silentDestroyRef.current) {
                    closeTour()
                }

                driverRef.current = null
            },
        }

        const driverInstance = driver(driverConfig)
        driverRef.current = driverInstance
        prepareInitialPopoverRef.current = true

        let firstFrame = 0
        let secondFrame = 0

        firstFrame = window.requestAnimationFrame(() => {
            secondFrame = window.requestAnimationFrame(() => {
                driverInstance.drive(Math.min(mergedCurrent, availableSteps.length - 1))

                if (zIndex) {
                    const overlay = document.querySelector<SVGSVGElement>(".driver-overlay")
                    if (overlay) {
                        overlay.style.zIndex = String(zIndex)
                    }
                }
            })
        })

        return () => {
            window.cancelAnimationFrame(firstFrame)
            window.cancelAnimationFrame(secondFrame)
            if (preparePopoverFrameRef.current) {
                window.cancelAnimationFrame(preparePopoverFrameRef.current)
                preparePopoverFrameRef.current = undefined
            }
            prepareInitialPopoverRef.current = false
            silentDestroyRef.current = true
            driverInstance.destroy()
            silentDestroyRef.current = false
            resetProgressOnDestroyRef.current = false
            driverRef.current = null
        }
    }, [mergedOpen, driveSteps, availableSteps.length])

    useEffect(() => {
        const driverInstance = driverRef.current

        if (!mergedOpen || !driverInstance || driverInstance.getActiveIndex() === mergedCurrent) return

        driverInstance.moveTo(Math.min(mergedCurrent, availableSteps.length - 1))
    }, [mergedCurrent, mergedOpen, availableSteps.length])

    return null
}

export default Tour
